/**
 * How many appointments remain to be scheduled for a prescription line.
 *
 * `TherapyRxDto.amount` and `.scheduledCount` are both counted in prescribed
 * UNITS (Behandlungseinheiten), not appointments — see REVIEW.md: "Frequency
 * counts visits, not units." A Doppelbehandlung line (`unitsPerDay > 1`) packs
 * more than one unit into a single appointment, while the backend's
 * `AppointmentTemplateDto.amount` (MD-348) is read as the number of
 * APPOINTMENTS to create, one per `Array.from({ length: amount })` entry. So
 * units remaining must be divided by `unitsPerDay` before it is sent — passing
 * units straight through doubles the appointments (and the billed units) for
 * any doubled line.
 *
 * Floors rather than rounds: an odd remainder (a partial day already
 * scheduled outside the normal pattern) should under- rather than
 * over-schedule — an operator can always add one more appointment by hand,
 * but there is no undo for one created in error.
 */
export function remainingAppointmentCount(
  amount: number | null | undefined,
  scheduledCount: number | null | undefined,
  unitsPerDay: number | null | undefined,
): number {
  const unitsRemaining = (amount ?? 0) - (scheduledCount ?? 0);
  const perDay = unitsPerDay && unitsPerDay > 0 ? unitsPerDay : 1;
  return Math.max(0, Math.floor(unitsRemaining / perDay));
}
